import React from 'react';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';

import * as Headless from '@headlessui/react';
import {
  ArchiveBoxIcon,
  ArrowUturnLeftIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Spinner } from '@oysterjs/ui/spinner';
import { PageTitle } from '@oysterjs/ui/page';
import { DataTable } from '@oysterjs/ui/data-table';

import { SearchFilterInput, useFiltersFromLocalStorage } from '@oysterjs/ui/search-filter';
import {
  type WorkspaceFilter,
  createFilterDefinitions,
  activeFiltersToSearchFilter
} from '~/api/data/workspaces/copilot-filters';
import {
  getWorkspaceColumns,
  workspaceSortToCaraWorkspaceSortInput,
  type WorkspaceSortKey
} from '~/components/workspaces/copilot-columns';
import {
  type VisibilityConfigInput,
  UpdateCaraWorkspacesDocument,
  useSearchCaraWorkspacesQuery
} from '~/api/graphql';
import { UPDATE_CARA_WORKSPACE, usePermissions } from '~/components/permissions';
import { ArchivedViewTitle } from '~/components/archived-view-title';
import { Button } from '@oysterjs/ui/button';
import { DialogTitle, DialogDescription, Dialog, DialogActions } from '@oysterjs/ui/dialog';
import { client } from '~/api/client';
import { BulkVisibilityDialog } from '~/components/visibility/bulk-visibility-dialog';
import { useVisibilityEntitlement } from '~/components/visibility/use-visibility-entitlement';

const EmptyState = (props: {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement> & { className?: string }>;
  title: string;
  description: string;
}) => (
  <div className="rounded-lg bg-white shadow px-4 sm:px-6 py-12 text-center dark:bg-neutral-800">
    <props.icon className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
    <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">{props.title}</h3>
    <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{props.description}</p>
  </div>
);

const SelectionActionBar = ({
  selectedWorkspaces,
  setSelectedWorkspaces,
  loading,
  archived,
  visibilityEnabled,
  visibilityLoading,
  onVisibility,
  onArchive,
  onRestore
}: {
  selectedWorkspaces: string[];
  setSelectedWorkspaces: React.Dispatch<React.SetStateAction<string[]>>;
  loading: boolean;
  archived: boolean;
  visibilityEnabled: boolean;
  visibilityLoading: boolean;
  onVisibility: () => void;
  onArchive: () => void;
  onRestore: () => void;
}) => (
  <AnimatePresence>
    {selectedWorkspaces.length > 0 && (
      <motion.div
        className="fixed left-1/2 lg:left-[calc(8rem+50vw)] bottom-12 shadow-xl border border-neutral-100 dark:border-neutral-700 rounded-full bg-white dark:bg-neutral-800 p-2 z-10 flex gap-2"
        initial={{ opacity: 0, translateX: '-50%', translateY: 100, scale: 0.9 }}
        animate={{ opacity: 1, translateX: '-50%', translateY: 0, scale: 1 }}
        exit={{ opacity: 0, translateX: '-50%', translateY: 100, scale: 0.9 }}
        transition={{ duration: 0.15, ease: 'easeInOut' }}
      >
        <Headless.Button
          className={clsx(
            'flex items-center gap-x-1 rounded-full px-3 py-1.5 text-xs font-medium flex-shrink-0',
            'focus:outline-none after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:ring-inset after:ring-transparent after:focus-within:ring-0 after:focus-within:ring-neutral-300 dark:after:focus-within:ring-white/20',
            'text-white bg-blue-600/80 border border-transparent hover:bg-blue-600/90'
          )}
          onClick={() => setSelectedWorkspaces([])}
        >
          <div>{selectedWorkspaces.length} selected</div>
          <div>
            <XMarkIcon className="size-3.5" />
          </div>
        </Headless.Button>
        {archived ? (
          <Headless.Button
            className={clsx(
              'flex items-center gap-x-1 rounded-full px-3 py-1.5 text-xs font-medium flex-shrink-0',
              'focus:outline-none after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:ring-inset after:ring-transparent after:focus-within:ring-0 after:focus-within:ring-neutral-300 dark:after:focus-within:ring-white/20',
              !loading &&
                'text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 border-dashed hover:bg-neutral-100/50 dark:hover:bg-white/10',
              loading &&
                'text-neutral-500 dark:text-neutral-400 bg-neutral-50/50 dark:bg-neutral-900/50 border border-neutral-200 dark:border-neutral-700 border-dashed cursor-default'
            )}
            onClick={onRestore}
          >
            <ArrowUturnLeftIcon className="size-3.5" />
            Restore
            {loading && <Spinner size="size-3.5" color="neutral" />}
          </Headless.Button>
        ) : (
          <>
            {visibilityEnabled && (
              <Headless.Button
                className={clsx(
                  'flex items-center gap-x-1 rounded-full px-3 py-1.5 text-xs font-medium flex-shrink-0',
                  'focus:outline-none after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:ring-inset after:ring-transparent after:focus-within:ring-0 after:focus-within:ring-neutral-300 dark:after:focus-within:ring-white/20',
                  !visibilityLoading &&
                    'text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 border-dashed hover:bg-neutral-100/50 dark:hover:bg-white/10',
                  visibilityLoading &&
                    'text-neutral-500 dark:text-neutral-400 bg-neutral-50/50 dark:bg-neutral-900/50 border border-neutral-200 dark:border-neutral-700 border-dashed cursor-default'
                )}
                onClick={onVisibility}
                disabled={visibilityLoading}
                title="Change visibility"
              >
                <EyeIcon className="size-3.5" />
                Visibility
                {visibilityLoading && <Spinner size="size-3.5" color="neutral" />}
              </Headless.Button>
            )}
            <Headless.Button
              className={clsx(
                'flex items-center gap-x-1 rounded-full px-3 py-1.5 text-xs font-medium flex-shrink-0',
                'focus:outline-none after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:ring-inset after:ring-transparent after:focus-within:ring-0 after:focus-within:ring-neutral-300 dark:after:focus-within:ring-white/20',
                !loading &&
                  'text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 border-dashed hover:bg-neutral-100/50 dark:hover:bg-white/10',
                loading &&
                  'text-neutral-500 dark:text-neutral-400 bg-neutral-50/50 dark:bg-neutral-900/50 border border-neutral-200 dark:border-neutral-700 border-dashed cursor-default'
              )}
              onClick={onArchive}
            >
              <TrashIcon className="size-3.5" />
              Archive
              {loading && <Spinner size="size-3.5" color="neutral" />}
            </Headless.Button>
          </>
        )}
      </motion.div>
    )}
  </AnimatePresence>
);

const SEARCH_WORKSPACES_PAGE_SIZE = 100;

export const CopilotWorkspacesPage = ({ archived = false }: { archived?: boolean } = {}) => {
  const [loading, setLoading] = React.useState(false);
  const [visibilityLoading, setVisibilityLoading] = React.useState(false);
  const visibilityEnabled = useVisibilityEntitlement();

  const filterDefinitions = React.useMemo(() => createFilterDefinitions(), []);

  const { activeFilters, setActiveFilters, initialFilters, sortBy, sortDirection, setSort } =
    useFiltersFromLocalStorage<WorkspaceFilter, WorkspaceSortKey>(
      archived ? 'cara_workspaces_archived_view' : 'cara_workspaces_default_view',
      filterDefinitions,
      { defaultSortDirection: 'desc' }
    );

  const filterInput = React.useMemo(
    () => activeFiltersToSearchFilter(activeFilters),
    [activeFilters]
  );
  // In archived mode, layer `archived: true` on top of any active filters so the
  // query returns only archived workspaces.
  const queryFilter = React.useMemo(
    () => (archived ? { ...(filterInput ?? {}), archived: true } : (filterInput ?? undefined)),
    [archived, filterInput]
  );
  const sortInput = React.useMemo(
    () => workspaceSortToCaraWorkspaceSortInput(sortBy, sortDirection),
    [sortBy, sortDirection]
  );

  const {
    data: searchData,
    loading: searchLoading,
    fetchMore,
    refetch
  } = useSearchCaraWorkspacesQuery({
    variables: {
      first: SEARCH_WORKSPACES_PAGE_SIZE,
      filter: queryFilter,
      sort: sortInput
    },
    notifyOnNetworkStatusChange: true
  });

  const connection = searchData?.searchCaraWorkspaces;
  const workspaceNodes = React.useMemo(
    () => (connection?.edges ?? []).map((e) => e.node),
    [connection?.edges]
  );
  const hasNextPage = connection?.pageInfo.hasNextPage ?? false;
  const endCursor = connection?.pageInfo.endCursor ?? null;
  const totalCount = connection?.totalCount ?? 0;

  const loadMore = React.useCallback(() => {
    if (!endCursor || !hasNextPage) return;
    fetchMore({
      variables: {
        after: endCursor,
        first: SEARCH_WORKSPACES_PAGE_SIZE,
        filter: queryFilter,
        sort: sortInput
      }
    });
  }, [endCursor, hasNextPage, fetchMore, queryFilter, sortInput]);

  // Selecting rows only leads to bulk archive, restore and visibility, all of
  // which are workspace updates.
  const canUpdate = usePermissions().hasAllPermissions(UPDATE_CARA_WORKSPACE);

  const [selectedWorkspaces, setSelectedWorkspaces] = React.useState<string[]>([]);
  const selectedSet = React.useMemo(() => new Set(selectedWorkspaces), [selectedWorkspaces]);
  const [showArchiveDialog, setShowArchiveDialog] = React.useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = React.useState(false);
  const [showVisibilityDialog, setShowVisibilityDialog] = React.useState(false);

  // Archive and restore both go through UpdateCaraWorkspaces and then refetch so
  // the list reflects the change instantly, mirroring the visibility flow.
  const archiveWorkspaces = async (workspaceIds: string[]) => {
    setLoading(true);
    try {
      await client.mutate({
        mutation: UpdateCaraWorkspacesDocument,
        variables: { workspaceIds, input: { archive: true } }
      });
      setShowArchiveDialog(false);
      setSelectedWorkspaces([]);
      await refetch();
    } finally {
      setLoading(false);
    }
  };

  const restoreWorkspaces = async (workspaceIds: string[]) => {
    setLoading(true);
    try {
      await client.mutate({
        mutation: UpdateCaraWorkspacesDocument,
        variables: { workspaceIds, input: { archive: false } }
      });
      setShowRestoreDialog(false);
      setSelectedWorkspaces([]);
      await refetch();
    } finally {
      setLoading(false);
    }
  };

  const updateWorkspaceVisibility = async (visibility: VisibilityConfigInput) => {
    setVisibilityLoading(true);
    try {
      await client.mutate({
        mutation: UpdateCaraWorkspacesDocument,
        variables: {
          workspaceIds: selectedWorkspaces,
          input: { visibility }
        }
      });
      setShowVisibilityDialog(false);
      setSelectedWorkspaces([]);
      await refetch();
    } finally {
      setVisibilityLoading(false);
    }
  };

  const columns = React.useMemo(
    () =>
      getWorkspaceColumns({
        selectedSet,
        setSelectedWorkspaces,
        filtered: workspaceNodes,
        selectable: canUpdate
      }),
    [canUpdate, selectedSet, workspaceNodes]
  );

  return (
    <PageTitle
      title={
        <ArchivedViewTitle
          archived={archived}
          title="Workspaces"
          archivedTitle="Archived workspaces"
          basePath="/app/workspaces"
          noun="workspaces"
        />
      }
      description={
        archived
          ? "Workspaces you've archived. Restore any to bring them back."
          : "Copilot workspaces you've created to interact with Cara"
      }
    >
      <div className="space-y-4">
        <SearchFilterInput
          filterDefinitions={filterDefinitions}
          onFiltersChange={setActiveFilters}
          initialFilters={initialFilters}
          placeholder="Filter by status, user, preset, date, or search..."
        />

        {searchLoading && workspaceNodes.length === 0 ? (
          <CopilotWorkspacesPageLoader />
        ) : (
          <div className="relative">
            {canUpdate && (
              <SelectionActionBar
                selectedWorkspaces={selectedWorkspaces}
                setSelectedWorkspaces={setSelectedWorkspaces}
                loading={loading}
                archived={archived}
                visibilityEnabled={visibilityEnabled}
                visibilityLoading={visibilityLoading}
                onVisibility={() => setShowVisibilityDialog(true)}
                onArchive={() => setShowArchiveDialog(true)}
                onRestore={() => setShowRestoreDialog(true)}
              />
            )}
            {totalCount === 0 ? (
              <EmptyState
                icon={
                  archived
                    ? ArchiveBoxIcon
                    : activeFilters.length > 0
                      ? MagnifyingGlassIcon
                      : SparklesIcon
                }
                title={archived ? 'No archived workspaces' : 'No workspaces found'}
                description={
                  archived
                    ? activeFilters.length > 0
                      ? 'Try adjusting your search or filters.'
                      : 'Workspaces you archive will show up here.'
                    : activeFilters.length > 0
                      ? 'Try adjusting your search or filters.'
                      : 'Create a copilot workspace to get started.'
                }
              />
            ) : (
              <div className="px-4 sm:px-6 rounded-lg bg-white shadow dark:bg-neutral-800">
                <DataTable
                  data={workspaceNodes}
                  columns={columns}
                  keyExtractor={(ws) => ws.id}
                  rowHref={archived ? undefined : (ws) => `/app/workspaces/${ws.id}`}
                  tableClassName="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
                  sortKey={sortBy}
                  sortDirection={sortDirection}
                  onSort={setSort}
                  loading={searchLoading}
                />
                {hasNextPage && (
                  <div className="px-4 py-1.5 text-center dark:border-neutral-700">
                    <span className="text-xs text-neutral-500 dark:text-neutral-400">
                      Showing {workspaceNodes.length} of {totalCount} workspaces.{' '}
                      <Headless.Button
                        type="button"
                        onClick={loadMore}
                        className="inline text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:underline font-medium"
                      >
                        Load more
                      </Headless.Button>
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <Dialog open={showArchiveDialog} onClose={() => setShowArchiveDialog(false)} size="md">
        <DialogTitle>Archive Workspaces</DialogTitle>
        <DialogDescription>
          Are you sure you want to archive <strong>{selectedWorkspaces.length}</strong>{' '}
          {selectedWorkspaces.length === 1 ? 'workspace' : 'workspaces'}? You can restore them later
          from the archived workspaces view.
        </DialogDescription>
        <DialogActions>
          <Button
            type="button"
            color="white"
            onClick={() => setShowArchiveDialog(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            color="sky"
            onClick={() => archiveWorkspaces(selectedWorkspaces)}
            loading={loading}
          >
            Archive
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showRestoreDialog} onClose={() => setShowRestoreDialog(false)} size="md">
        <DialogTitle>Restore workspaces</DialogTitle>
        <DialogDescription>
          Restore <strong>{selectedWorkspaces.length}</strong>{' '}
          {selectedWorkspaces.length === 1 ? 'workspace' : 'workspaces'}? They'll move back to your
          active workspaces.
        </DialogDescription>
        <DialogActions>
          <Button
            type="button"
            color="white"
            onClick={() => setShowRestoreDialog(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            color="sky"
            onClick={() => restoreWorkspaces(selectedWorkspaces)}
            loading={loading}
          >
            Restore
          </Button>
        </DialogActions>
      </Dialog>
      <BulkVisibilityDialog
        open={showVisibilityDialog}
        count={selectedWorkspaces.length}
        itemLabel="workspace"
        notice="This changes the selected workspaces only. Documents and other artifacts inside them keep their current visibility."
        loading={visibilityLoading}
        onClose={() => setShowVisibilityDialog(false)}
        onSave={updateWorkspaceVisibility}
      />
    </PageTitle>
  );
};

export const CopilotWorkspacesPageLoader = () => {
  return (
    <div className="px-6 rounded-lg bg-white shadow dark:bg-neutral-800">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
          Loading workspaces...
        </h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          This might take a moment.
        </p>
      </div>
    </div>
  );
};
